import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import {
  DisplaySettings,
  defaultDisplaySettings,
} from '../types/DisplaySettings';
import { SensorDevice } from '../types/SensorDevice';
import { SensorStatus } from '../types/SensorStatus';

const StyledCard = styled(Card, {
  // Keep custom styling props off the underlying DOM node (avoids React
  // "unknown prop" warnings in the console).
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'displaySettings',
})(
  ({
    status,
    displaySettings,
  }: {
    status: SensorStatus | null;
    displaySettings: DisplaySettings;
  }) => {
    const isPresent = status === SensorStatus.IN;
    const isAbsent = status === SensorStatus.OUT;
    const isMeeting = status === SensorStatus.MEETING;
    // Each status owns its background color (재실/부재중/회의중/기타), all
    // configurable from 화면 설정. Unknown/custom statuses fall back to "기타".
    const backgroundColor = isPresent
      ? displaySettings.presentBackgroundColor
      : isAbsent
      ? displaySettings.absentBackgroundColor
      : isMeeting
      ? displaySettings.meetingBackgroundColor
      : displaySettings.etcBackgroundColor;
    return {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      textAlign: 'center',
      boxShadow: '0 2px 8px 0 rgba(17, 24, 39, 0.08)',
      borderRadius: 0,
      backgroundColor,
      // A crisp neutral border keeps light cards separated from the #f7f8fa
      // page; the colored "present" card needs none.
      border: isPresent ? 'none' : '1px solid #e2e5ea',
      justifyContent: 'center',
    };
  },
);
export interface RoomCardProps {
  value: SensorDevice;
  isAdminOrManagerMode?: boolean;
  onEditClick?: (value: SensorDevice) => void;
  displaySettings?: DisplaySettings;
}
export function RoomCard({
  value,
  onEditClick,
  displaySettings = defaultDisplaySettings,
}: RoomCardProps) {
  const [hasMoved, setHasMoved] = useState(false);
  const [moveStartTime, setMoveStartTime] = useState(-1);
  // Clicking any card opens the room-info popup. Admins/managers can edit there;
  // guests get a read-only view (current status + 전달사항).
  const handleEditClick = useCallback(() => {
    onEditClick?.(value);
  }, [value, onEditClick]);
  const handleTouchStart = useCallback(() => {
    setHasMoved(false);
    setMoveStartTime(Date.now());
  }, []);
  const handleTouchMove = useCallback(() => {
    if (!hasMoved) {
      setHasMoved(true);
    }
  }, [hasMoved]);
  const handleTouchEnd = useCallback(() => {
    const isJustClick = Date.now() - moveStartTime < 100;
    if (!hasMoved || isJustClick) {
      handleEditClick();
    }
    setMoveStartTime(-1);
  }, [hasMoved, moveStartTime, handleEditClick]);
  const isPresent = value.status === SensorStatus.IN;
  // Keep the name (and position) on a single line: shrink the font only when
  // it would overflow the card width. Critical on mobile, where a wall-display
  // font size is far too big for the narrow single-column cards and would
  // otherwise wrap the name across two lines.
  const nameRef = useRef<HTMLSpanElement>(null);
  const positionRef = useRef<HTMLSpanElement>(null);
  const name = value.companyUser?.name;
  const position = value.companyUser?.position;
  useLayoutEffect(() => {
    const container = nameRef.current?.parentElement;
    if (!container) return;
    const fitOne = (el: HTMLSpanElement | null, baseSize: number) => {
      if (!el) return;
      el.style.fontSize = `${baseSize}px`;
      const avail = container.clientWidth;
      if (avail > 0 && el.scrollWidth > avail) {
        const size = Math.max(
          Math.floor(baseSize * (avail / el.scrollWidth)),
          12,
        );
        el.style.fontSize = `${size}px`;
      }
    };
    // Only re-fit when the available WIDTH changes. Re-fitting on every observed
    // resize would loop forever: shrinking the font changes the element height,
    // which the observer reports, which would re-fit, etc. (the "ResizeObserver
    // loop completed" error). rAF defers the work out of the observer callback.
    let lastWidth = -1;
    let raf = 0;
    const fit = () => {
      const w = container.clientWidth;
      if (w === lastWidth) return;
      lastWidth = w;
      fitOne(nameRef.current, displaySettings.cardTitleFontSize);
      fitOne(positionRef.current, displaySettings.cardPositionFontSize);
    };
    fit();
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    });
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [
    name,
    position,
    displaySettings.cardTitleFontSize,
    displaySettings.cardPositionFontSize,
  ]);
  return (
    <StyledCard
      status={value.status}
      displaySettings={displaySettings}
      onMouseDown={handleTouchStart}
      onMouseMove={handleTouchMove}
      onClick={handleTouchEnd}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          component="h2"
          sx={{
            color: isPresent ? '#fff' : 'inherit',
            lineHeight: 1.12,
            width: '100%',
          }}
        >
          <span
            ref={nameRef}
            style={{
              display: 'block',
              fontFamily:
                'Pretendard, AppleSDGothicNeo, -apple-system, sans-serif',
              fontWeight: 800,
              fontSize: `${displaySettings.cardTitleFontSize}px`,
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          {position ? (
            <span
              ref={positionRef}
              style={{
                display: 'block',
                marginTop: '0.2em',
                fontSize: `${displaySettings.cardPositionFontSize}px`,
                fontWeight: 500,
                opacity: 0.72,
                whiteSpace: 'nowrap',
              }}
            >
              {position}
            </span>
          ) : null}
        </Typography>
      </CardContent>
    </StyledCard>
  );
}
